import { useState } from 'react';
import { Image, Music, FileText, Castle } from 'lucide-react';

// 카테고리 타입 정의
type FilterType = 'all' | 'story' | 'image' | 'music' | 'writing';

interface WorkItem {
  id: number;
  type: 'story' | 'image' | 'music' | 'writing';
  title: string;
  fileUrl: string;
}

interface MonthlyData {
  items: WorkItem[];
}

const MonthlyWorksSection = () => {
  // 탭이 선택 상태
  const [activeTab, setActiveTab] = useState<FilterType>('all');

  const worksData: MonthlyData = {
    items: [
      {
        id: 1,
        type: 'story',
        title: '필리핀 바다의 마법 같은 하루',
        fileUrl: '/files/works_story01.pdf'
      },
      {
        id: 2,
        type: 'story',
        title: '바다로 간 꼬마 공룡 피온의 모험',
        fileUrl: '/files/works_story02.pdf'
      },
      {
        id: 3,
        type: 'image',
        title: '코코비치의 바다 친구들',
        fileUrl: '/files/works_img01.png'
      },
      {
        id: 4,
        type: 'music',
        title: '침대 위 햄스터댄스',
        fileUrl: '/files/works_music01.mp3'
      },
      {
        id: 5,
        type: 'music',
        title: '산바람 톡톡톡',
        fileUrl: '/files/works_music02.mp3'
      },
      {
        id: 6,
        type: 'music',
        title: '고양이랑 돼지의 잔디밭 파티',
        fileUrl: '/files/works_music03.mp3'
      },
      {
        id: 7,
        type: 'music',
        title: '첨벙첨벙',
        fileUrl: '/files/works_music04.mp3'
      },
      {
        id: 8,
        type: 'music',
        title: '달려 스케이스 스타',
        fileUrl: '/files/works_music05.mp3'
      },
      {
        id: 9,
        type: 'music',
        title: '예쁜 꿈',
        fileUrl: '/files/works_music06.mp3'
      },
      {
        id: 10,
        type: 'story',
        title: '마리오와 우주의 크리스마스 별',
        fileUrl: '/files/works_story03.pdf'
      }
    ]
  };

  // 선택된 탭에 따라 리스트 필터링
  const filteredItems =
    activeTab === 'all'
      ? worksData.items
      : worksData.items.filter((item) => item.type === activeTab);

  const getStyleByType = (type: string) => {
    switch (type) {
      case 'story':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-500',
          badge: 'bg-amber-100 text-amber-700',
          labelText: '동화',
          icon: <Castle size={44} />
        };
      case 'image':
        return {
          bg: 'bg-blue-50',
          text: 'text-blue-500',
          badge: 'bg-blue-100 text-blue-700',
          labelText: '이미지',
          icon: <Image size={44} />
        };
      case 'music':
        return {
          bg: 'bg-emerald-50',
          text: 'text-emerald-500',
          badge: 'bg-emerald-100 text-emerald-700',
          labelText: '음악',
          icon: <Music size={44} />
        };
      case 'writing':
        return {
          bg: 'bg-purple-50',
          text: 'text-purple-500',
          badge: 'bg-purple-100 text-purple-700',
          labelText: '글',
          icon: <FileText size={44} />
        };
      default:
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-500',
          badge: 'bg-gray-100 text-gray-700',
          labelText: '알 수 없음',
          icon: <FileText size={44} />
        };
    }
  };

  // 탭 메뉴 배열
  const tabMenus = [
    { type: 'all', label: '전체 보기' },
    { type: 'story', label: '📖 동화' },
    { type: 'image', label: '🎨 이미지' },
    { type: 'music', label: '🎧 음악' },
    { type: 'writing', label: '✍️ 글' }
  ] as const;

  return (
    <section id="monthly-works" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* 상단 타이틀 */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="text-blue-600">이달의 </span>
            라이브클래스 작품
          </h2>
          <p className="text-lg text-gray-500">
            매월 라이브 클래스를 통해 탄생한 우리 아이들의 멋진 AI 작품을
            만나보세요!
          </p>
        </div>

        {/* 상단 탭 메뉴 바 */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {tabMenus.map((menu) => (
            <button
              key={menu.type}
              onClick={() => setActiveTab(menu.type)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeTab === menu.type
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {menu.label}
            </button>
          ))}
        </div>

        {/* 카드 그리드 영역 */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredItems.map((work) => {
              const style = getStyleByType(work.type);
              return (
                <div
                  key={work.id}
                  className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 flex flex-col items-center text-center"
                  onClick={() => window.open(work.fileUrl, '_blank')}
                >
                  <div
                    className={`w-28 h-28 ${style.bg} ${style.text} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300`}
                  >
                    {style.icon}
                  </div>
                  <span
                    className={`${style.badge} px-2.5 py-1 rounded-full text-xs font-semibold mb-3`}
                  >
                    {style.labelText}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug">
                    {work.title}
                  </h3>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className=" text-gray-400">
              선택한 카테고리에 해당하는 작품이 없습니다.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MonthlyWorksSection;
